// @ts-nocheck
import clsx from "clsx";
import dayjs from "dayjs";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import BasicTable from "../components/BasicTable";
import MapComponent from "../components/map";
import useAccountEnhanced from "src/hooks/use-account-enhanced";
import {
    getCustomerHistory,
    getShippingInfo,
    updateShippingInfo,
} from "src/api/customer-service.ts";
import { useSearchParams } from "react-router-dom";
import { getZipCodeInfo } from "src/api/core.api.ts";
import toast from "react-hot-toast";
import { uspsApi } from "src/api/usps.api.ts";

type SearchZipCode = {
    postal_code: string;
    country_code: string;
    latitude: string;
    longitude: string;
    city: string;
    state: string;
    city_en: string;
    state_en: string;
    state_code: string;
    province: string;
    province_code: string;
    stateCharacterCode: string;
};

type SearchZipCodeResponse = {
    status: string;
    data: {
        zipCodeSearch: SearchZipCode[];
    };
};

type CustomerLocation = {
    CustomerCode: string;
    CustomerName: string;
    ShippingAddress_Street1: string;
    ShippingAddress_Street2: string;
    ShippingAddress_City: string;
    ShippingAddress_State: string;
    ShippingAddress_Zip: string;
    ShippingAddress_Zip4: string;
    ShippingAddress_Country: string;
    Latitude: number;
    Longitude: number;
};

type CustomerLocationResponse = {
    Data: CustomerLocation;
    Message: null;
    Status: "ok";
};

const customerLocationInitial: CustomerLocation = {
    CustomerCode: "",
    CustomerName: "",
    ShippingAddress_Street1: "",
    ShippingAddress_Street2: "",
    ShippingAddress_City: "",
    ShippingAddress_State: "",
    ShippingAddress_Zip: "",
    ShippingAddress_Zip4: "",
    ShippingAddress_Country: "",
    Latitude: 0,
    Longitude: 0,
};

function Geo() {
    const accountEnhanced = useAccountEnhanced();
    const [searchParams] = useSearchParams();
    const customerCode = searchParams.get("customerCode");
    const [customerCodeSearchLoading, setCustomerCodeSearchLoading] =
        useState(false);
    const [customerLocationOriginal, setCustomerLocationOriginal] =
        useState<CustomerLocation>(customerLocationInitial);
    const [customerLocation, setCustomerLocation] = useState<CustomerLocation>(
        customerLocationInitial,
    );
    const [history, setHistory] = useState(null);

    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isEditingCustomerLocation, setIsEditingCustomerLocation] =
        useState(false);

    const [zipCodeSearchLoading, setZipCodeSearchLoading] = useState(false);
    const [zipCodeSearchResults, setZipCodeSearchResults] = useState<
        | {
              city: string;
              state: string;
              zipCode: string;
              country: string;
          }[]
        | null
    >(null);
    const [saveCustomerLocationLoading, setSaveCustomerLocationLoading] =
        useState(false);

    async function loadCustomer(customerCode: string) {
        setIsEditingCustomerLocation(false);
        setCustomerLocationOriginal(customerLocationInitial);
        setCustomerLocation(customerLocationInitial);
        setZipCodeSearchResults(null);

        try {
            setCustomerCodeSearchLoading(true);

            const shippingInfo = await getShippingInfo(customerCode);

            setCustomerCodeSearchLoading(false);
            setCustomerLocationOriginal(shippingInfo);
            setCustomerLocation(shippingInfo);
        } catch (error) {
            setCustomerCodeSearchLoading(false);
            toast.error(error.message);
        }
    }

    const handleSearchSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const customerCode = e.currentTarget.iCustomerCode.value.trim();

        if (!customerCode) return;

        loadCustomer(customerCode);
    };

    const handleViewHistoryClick = async () => {
        try {
            const customerHistory = await getCustomerHistory(
                customerLocation.CustomerCode,
            );
            setHistory(customerHistory);
            setIsHistoryOpen(true);
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const handleSaveCustomerLocation = async () => {
        try {
            setSaveCustomerLocationLoading(true);
            const newShippingInfo = await updateShippingInfo(
                customerLocation.CustomerCode,
                {
                    UpdateByUser: accountEnhanced.me.employeeCode,
                    ShippingAddress1: customerLocation.ShippingAddress_Street1,
                    ShippingAddress2: customerLocation.ShippingAddress_Street2,
                    ShippingCity: customerLocation.ShippingAddress_City,
                    ShippingState: customerLocation.ShippingAddress_State,
                    ShippingZip: customerLocation.ShippingAddress_Zip,
                    ShippingZip2: "", // string
                    ShippingCountry: customerLocation.ShippingAddress_Country,
                    AreaCode: 0, // int
                    Phone: 0, // int
                    Latitude: customerLocation.Latitude,
                    Longitude: customerLocation.Longitude,
                },
            );

            setCustomerLocationOriginal(newShippingInfo);
            setCustomerLocation(newShippingInfo);
            setIsEditingCustomerLocation(false);
            setSaveCustomerLocationLoading(false);
        } catch (error) {
            setSaveCustomerLocationLoading(false);
        }
    };

    function handleEditCustomerLocationClick() {
        setIsEditingCustomerLocation(true);
    }

    function handleCancelEditCustomerLocationClick() {
        setCustomerLocation(customerLocationOriginal);
        setZipCodeSearchResults(null);
        setIsEditingCustomerLocation(false);
    }

    async function handleVerifyClick() {
        if (
            customerLocation === null ||
            customerLocation?.ShippingAddress_Zip === null ||
            customerLocation?.ShippingAddress_Country === null
        )
            return;

        try {
            setZipCodeSearchLoading(true);

            if (customerLocation.ShippingAddress_Country === "USA") {
                const zipCodeLookupResult = await uspsApi.zipCodeLookup({
                    body: { zipCode: customerLocation.ShippingAddress_Zip },
                });

                const zipCodeLookupNormalized = [
                    {
                        city: zipCodeLookupResult.defaultCity,
                        state: zipCodeLookupResult.defaultState,
                        zipCode: customerLocation.ShippingAddress_Zip,
                        country: "US",
                    },
                    ...zipCodeLookupResult.citiesList.map((city) => {
                        return {
                            city: city.city,
                            state: city.state,
                            zipCode: customerLocation.ShippingAddress_Zip,
                            country: "US",
                        };
                    }),
                ];

                setZipCodeSearchResults(zipCodeLookupNormalized);
            } else {
                const zipCodeInfo = await getZipCodeInfo({
                    zipCode: customerLocation.ShippingAddress_Zip,
                    country: { USA: "US", MEX: "MX", CAN: "CA" }[
                        customerLocation.ShippingAddress_Country
                    ],
                });
                setZipCodeSearchResults(
                    zipCodeInfo.map((item) => {
                        return {
                            city: item.city,
                            state: item.state,
                            zipCode: item.postal_code,
                            country: item.country_code,
                        };
                    }),
                );
            }

            setZipCodeSearchLoading(false);
        } catch (error) {
            console.error("Error fetching data: ", error);
            setZipCodeSearchLoading(false);
        }
    }

    function handleChange(
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) {
        setCustomerLocation((prev) => {
            return {
                ...prev,
                [e.target.name]: e.target.value,
            };
        });
    }

    function handleZipCodeSearchResultClick(zipCodeSearchResult: {
        city: string;
        state: string;
        zipCode: string;
        country: string;
    }) {
        setCustomerLocation((prev) => {
            return {
                ...prev,
                ShippingAddress_City: zipCodeSearchResult.city.toUpperCase(),
                ShippingAddress_State: zipCodeSearchResult.state.toUpperCase(),
                ShippingAddress_Zip: zipCodeSearchResult.zipCode,
                ShippingAddress_Country: { US: "USA", MX: "MEX", CA: "CAN" }[
                    zipCodeSearchResult.country
                ]!,
            };
        });

        setZipCodeSearchResults(null);
    }

    useEffect(() => {
        if (customerCode) {
            loadCustomer(customerCode);
        }
    }, [customerCode]);

    return (
        <>
            <div className="p-4 h-full flex flex-col">
                <form
                    className="flex items-end space-x-2 mb-4"
                    onSubmit={handleSearchSubmit}
                >
                    <fieldset className="fieldset w-full max-w-xs py-0">
                        <label className="label">
                            <span className="text-white">Customer Code</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Customer code"
                            className="input input-bordered input-sm w-full max-w-xs"
                            id="iCustomerCode"
                            name="iCustomerCode"
                            maxLength={6}
                            defaultValue={customerCode ?? ""}
                        />
                    </fieldset>

                    <button type="submit" className="btn btn-sm btn-primary">
                        {customerCodeSearchLoading ? (
                            <span className="loading loading-spinner"></span>
                        ) : (
                            "Find"
                        )}
                    </button>
                </form>
                {customerLocation.CustomerCode ? (
                    <div className="grid grid-cols-12 gap-x-4 flex-1">
                        <div className="col-span-6">
                            <div className="card bg-gray-700 flex flex-col h-full">
                                <div className="card-body">
                                    <div className="h-1 grow overflow-y-scroll no-scrollbar">
                                        {/* Customer Location */}
                                        <div>
                                            <div className="flex justify-between">
                                                <div className="text-xl text-secondary font-semibold">
                                                    Customer Location Details
                                                </div>
                                                {isEditingCustomerLocation ? (
                                                    <div className="space-x-2">
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={
                                                                handleSaveCustomerLocation
                                                            }
                                                            disabled={
                                                                saveCustomerLocationLoading
                                                            }
                                                        >
                                                            {saveCustomerLocationLoading ? (
                                                                <span className="loading w-3 loading-spinner"></span>
                                                            ) : (
                                                                "Save"
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-error"
                                                            onClick={
                                                                handleCancelEditCustomerLocationClick
                                                            }
                                                            disabled={
                                                                saveCustomerLocationLoading
                                                            }
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className={clsx(
                                                            "btn btn-sm btn-secondary",
                                                        )}
                                                        onClick={
                                                            handleEditCustomerLocationClick
                                                        }
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                            <form className="grid grid-cols-12 gap-x-4">
                                                {/* Shipping Address 1 */}
                                                <fieldset className="fieldset w-full col-span-12">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            Shipping Address 1
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={25}
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="ShippingAddress_Street1"
                                                        value={
                                                            customerLocation.ShippingAddress_Street1
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* Shipping Address 2 */}
                                                <fieldset className="fieldset w-full col-span-12">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            Shipping Address 2
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={25}
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="ShippingAddress_Street2"
                                                        value={
                                                            customerLocation.ShippingAddress_Street2
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* City */}
                                                <fieldset className="fieldset w-full col-span-5">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            City
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="ShippingAddress_City"
                                                        value={
                                                            customerLocation.ShippingAddress_City
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* State */}
                                                <fieldset className="fieldset w-full col-span-2">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            State
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="ShippingAddress_State"
                                                        maxLength={2}
                                                        value={
                                                            customerLocation.ShippingAddress_State
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* ZipCode */}
                                                <fieldset className="fieldset w-full col-span-2">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            Zip Code
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="ShippingAddress_Zip"
                                                        maxLength={7}
                                                        value={
                                                            customerLocation.ShippingAddress_Zip
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* Country */}
                                                <fieldset className="fieldset w-full col-span-2">
                                                    <label className="label">
                                                        <span className="label-text text-white">
                                                            Country
                                                        </span>
                                                    </label>
                                                    <select
                                                        className="select select-bordered select-sm disabled:text-primary-content"
                                                        name="ShippingAddress_Country"
                                                        value={
                                                            customerLocation.ShippingAddress_Country
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    >
                                                        <option value=""></option>
                                                        <option value="USA">
                                                            USA
                                                        </option>
                                                        <option value="MEX">
                                                            MEX
                                                        </option>
                                                        <option value="CAN">
                                                            CAN
                                                        </option>
                                                    </select>
                                                </fieldset>

                                                <button
                                                    type="button"
                                                    onClick={handleVerifyClick}
                                                    className="btn btn-sm btn-secondary self-end col-span-1 flex justify-center items-center p-0"
                                                    disabled={
                                                        !isEditingCustomerLocation ||
                                                        zipCodeSearchLoading ||
                                                        saveCustomerLocationLoading
                                                    }
                                                >
                                                    {zipCodeSearchLoading ? (
                                                        <span className="loading loading-spinner"></span>
                                                    ) : (
                                                        <IoIosCheckmarkCircleOutline className="text-xl" />
                                                    )}
                                                </button>

                                                {zipCodeSearchResults !==
                                                null ? (
                                                    <div className="col-span-12 bg-secondary mt-4 mb-1 rounded-sm py-3 px-4">
                                                        <div className="font-bold text-primary-content mb-1">
                                                            Verified Results
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            {zipCodeSearchResults.length ===
                                                            0 ? (
                                                                <div className="px-4 py-1 rounded-sm bg-primary/70 text-primary-content">
                                                                    No results
                                                                    found
                                                                </div>
                                                            ) : null}

                                                            {zipCodeSearchResults.map(
                                                                (item, idx) => (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="px-4 py-1 rounded-sm bg-primary/70 hover:bg-primary text-primary-content cursor-pointer"
                                                                        onClick={() =>
                                                                            handleZipCodeSearchResultClick(
                                                                                item,
                                                                            )
                                                                        }
                                                                    >
                                                                        {
                                                                            item.city
                                                                        }
                                                                        ,{" "}
                                                                        {
                                                                            item.state
                                                                        }{" "}
                                                                        {
                                                                            item.zipCode
                                                                        }
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : null}

                                                {/* Latitude */}
                                                <fieldset className="fieldset w-full col-span-6">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            Latitude
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="Latitude"
                                                        value={
                                                            customerLocation.Latitude
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>

                                                {/* Longitude */}
                                                <fieldset className="fieldset w-full col-span-6">
                                                    <label className="label">
                                                        <span className="text-white">
                                                            Longitude
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full disabled:text-primary-content"
                                                        name="Longitude"
                                                        value={
                                                            customerLocation.Longitude
                                                        }
                                                        onChange={handleChange}
                                                        disabled={
                                                            !isEditingCustomerLocation
                                                        }
                                                    />
                                                </fieldset>
                                            </form>
                                        </div>
                                        {isHistoryOpen ? (
                                            <button
                                                className="btn btn-error btn-sm mt-4"
                                                onClick={() =>
                                                    setIsHistoryOpen(false)
                                                }
                                            >
                                                Close History
                                            </button>
                                        ) : (
                                            <div className="mt-4">
                                                <button
                                                    onClick={
                                                        handleViewHistoryClick
                                                    }
                                                    className="btn btn-secondary btn-sm"
                                                >
                                                    View History
                                                </button>
                                            </div>
                                        )}

                                        {isHistoryOpen && (
                                            <div className="mt-4">
                                                <div>
                                                    <div className="text-white">
                                                        Lat/Long
                                                    </div>
                                                    <BasicTable
                                                        data={history.LocationHistory.map(
                                                            (item) => {
                                                                return [
                                                                    item.IccCustomerLocationsHistoryId,
                                                                    item.Latitude,
                                                                    item.Longitude,
                                                                    dayjs(
                                                                        item.EtlLastModifiedDate,
                                                                    ).format(
                                                                        "MM/DD/YYYY hh:mm A",
                                                                    ),
                                                                    item.ModifiedBy,
                                                                    item.EtlCurrentRow
                                                                        ? "Yes"
                                                                        : "No",
                                                                ];
                                                            },
                                                        )}
                                                        headings={[
                                                            "Id",
                                                            "Latitude",
                                                            "Longitude",
                                                            "ModifiedDate",
                                                            "User",
                                                            "Current",
                                                        ]}
                                                    />
                                                </div>
                                                <div className="mt-4">
                                                    <div className="text-white">
                                                        Geofence
                                                    </div>
                                                    <BasicTable
                                                        data={history.PolygonHistory.map(
                                                            (item) => {
                                                                return [
                                                                    item.CustomerPolygonsId,
                                                                    dayjs(
                                                                        item.ValidFromUtc,
                                                                    ).format(
                                                                        "MM/DD/YYYY hh:mm A",
                                                                    ),
                                                                    item.ValidTillUtc
                                                                        ? dayjs(
                                                                              item.ValidTillUtc,
                                                                          ).format(
                                                                              "MM/DD/YYYY hh:mm A",
                                                                          )
                                                                        : "",
                                                                    item.ModifiedByUser,
                                                                ];
                                                            },
                                                        )}
                                                        headings={[
                                                            "Id",
                                                            "Valid From",
                                                            "Valid Till",
                                                            "User",
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-6">
                            <MapComponent
                                customerCode={customerLocation.CustomerCode}
                                address={`${customerLocation.ShippingAddress_Street1}, ${customerLocation.ShippingAddress_City}, ${customerLocation.ShippingAddress_State} ${customerLocation.ShippingAddress_Zip}`}
                                coordinates={{
                                    lat: customerLocation.Latitude,
                                    lng: customerLocation.Longitude,
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="card bg-gray-700 flex-1 flex justify-center items-center">
                        {customerCodeSearchLoading ? (
                            <div className="text-white">
                                <span className="loading loading-spinner"></span>
                                loading
                            </div>
                        ) : (
                            <div className="text-white font-semibold text-xl">
                                Please enter customer code
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default Geo;
